// 此自定义 Hook 封装了所有与 Directus SDK 的交互
import {
  createDirectus, staticToken, rest,
  readCollections, createCollection, updateCollection, deleteCollection,
  readFieldsByCollection, createField, updateField, deleteField,
  readItems, createItems, createItem, updateItem, deleteItem,
  readPresets, createPreset, updatePreset,
  utilitySort,
  readPolicies, createPolicy, updatePolicy, readPermissions,
  deletePreset, uploadFiles, readFile, deleteFile,authentication
} from "@directus/sdk";
import { useMemo } from "react";
import { GridPluginProps, UserInfo } from "../types";

export function useDirectusAPI(props: GridPluginProps) {
  // const { apiUrl, adminToken } = props;
  const apiUrl = localStorage.baseUrl
  const adminToken = (window as any).AGIMX_CONFIG.CORE_TOKEN;
  const userId = useMemo(() => sessionStorage.getItem("agimx_user_id") || '', []);
  const userInfo: UserInfo = useMemo(() => ({
    first_name: sessionStorage.getItem("first_name") || '',
    last_name: sessionStorage.getItem("last_name") || '',
  }), []);
  const directus = useMemo(() => {
    if (!apiUrl) return null;
    return createDirectus(apiUrl).with(rest());
  }, [apiUrl]);

  const adminDirectus = useMemo(() => {
    if (!apiUrl || !adminToken) return null;
    return createDirectus(apiUrl).with(staticToken(adminToken)).with(rest());
  }, [apiUrl, adminToken]);

  const api = useMemo(() => {
    if (!directus || !adminDirectus) return null;
    return {
      // Sheet/Collection Operations
      getSheets: () => directus.request(readCollections()),
      createCollection: (payload: any) => adminDirectus.request(createCollection(payload)),
      renameSheet: (collection: string, newName: string) => adminDirectus.request(updateCollection(collection, { meta: { translations: [{ language: 'zh-CN', translation: newName }] } })),
      deleteCollection: (collection: string) => adminDirectus.request(deleteCollection(collection)),

      // Field/Column Operations
      getFields: (collection: string) => directus.request(readFieldsByCollection(collection)),
      createField: (collection: string, payload: any) => adminDirectus.request(createField(collection, payload)),
      updateField: (collection: string, field: string, payload: any) => adminDirectus.request(updateField(collection, field, payload)),
      deleteField: (collection: string, field: string) => adminDirectus.request(deleteField(collection, field)),

      // Item/Row Operations
      getRecords: (collection: string, fields: string[]) => directus.request(readItems(collection, { fields, sort: ['sort'], limit: -1 })),
      createRecord: (collection: string, payload: any) => adminDirectus.request(createItem(collection, payload)),
      createRecords: (collection: string, payload: any[]) => adminDirectus.request(createItems(collection, payload)),
      updateRecord: (collection: string, id: number, payload: any) => directus.request(updateItem(collection, id, payload)),
      deleteRecord: (collection: string, id: number) => directus.request(deleteItem(collection, id)),
      sortRecord: (collection: string, fromId: number, toId: number) => directus.request(utilitySort(collection, fromId, toId)),

      // Presets
      getPresets: () => directus.request(readPresets()),
      getPreset: (collection: string) => directus.request(readPresets( { filter: { collection: { _eq: collection } } })),
      getPresetsDetail: (payload: any) => directus.request(readPresets(payload)),
      createPreset: (payload: any) => directus.request(createPreset(payload)),
      updatePreset: (id: number, payload: any) => directus.request(updatePreset(id, payload)),
      deletePreset: (id: number) => directus.request(deletePreset(id)),

      // Permissions & Policies
      getPolicies: (params?: any) => directus.request(readPolicies(params)),
      createPolicy: (payload: any) => directus.request(createPolicy(payload)),
      updatePolicy: (id: string, payload: any) => directus.request(updatePolicy(id, payload)),
      getPermissions: (params?: any) => directus.request(readPermissions(params)),

      // Dashboard Operations
      getDashboards: () => directus.request(readItems('dashboard')),
      createDashboard: (payload: any) => directus.request(createItem('dashboard', payload)),
      updateDashboard: (id: number, payload: any) => directus.request(updateItem('dashboard', id, payload)),
      deleteDashboard: (id: number) => directus.request(deleteItem('dashboard', id)),
      getDashboardCharts: (dashboardId: number) => directus.request(readItems('dashboard_charts', { filter: { parentId: { _eq: dashboardId } } })),
      createDashboardChart: (payload: any) => directus.request(createItem('dashboard_charts', payload)),
      updateDashboardChart: (id: number, payload: any) => directus.request(updateItem('dashboard_charts', id, payload)),
      deleteDashboardChart: (id: number) => directus.request(deleteItem('dashboard_charts', id)),
      getDashboardPermission: (dashboardId: number, userId: string|null) => directus.request(readItems('dashboard_permissions', { filter: { dashboard_id: { _eq: dashboardId }, user_id: { _eq: userId} } })),
      createDashboardPermission: (payload: any) => directus.request(createItem('dashboard_permissions', payload)),
      updateDashboardPermission: (id: number, payload: any) => directus.request(updateItem('dashboard_permissions', id, payload)),

      // file
      createFile: (payload: any) => directus.request(uploadFiles(payload)),
      deleteFile: (id: string) => directus.request(deleteFile(id)),
      getFile: (id: string) => directus.request(readFile(id)),
    };
  }, [directus, adminDirectus]);

  return { userId, userInfo, api, directus, adminDirectus };
}
export {};